import Spinner from '@/components/Spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ColumnDef, flexRender, Table as ReactTableType } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';

interface DataTableProps<TData, TValue> {
  table: ReactTableType<TData>;
  columns: ColumnDef<TData, TValue>[];
  loading: boolean;
  error: string | null;
}

export function DataTable<TData, TValue>({
  columns,
  loading,
  error,
  table,
}: DataTableProps<Employee, TValue>) {
  const navigate = useNavigate();
  // Loading or error states
  if (loading)
    return (
      <div className='h-[67vh] flex items-center justify-center'>
        <Spinner />
      </div>
    );
  if (error)
    return (
      <div className='h-[67vh] flex items-center justify-center'>{error}</div>
    );

  const handleCellClick = (cell: any, row: any) => {
    // Check if the cell is not in the actions column (replace 'actions' with the actual column ID if different)
    if (cell.column.id !== 'actions') {
      // Navigate to the client detail page (you can pass the client ID or any identifier from the row data)
      navigate(`/collection/employee/${row.original.id}`); // Assuming row.original contains the client data
    }
  };

  return (
    <div className='flex flex-col min-h-[77vh]'>
      <div className=' rounded-[10px] border-[#ECEEF6] bg-white'>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead className='w-1/5' key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      onClick={() => handleCellClick(cell, row)}
                      className='w-1/5 font-normal hover:cursor-pointer'
                      key={cell.id}>

                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className='h-24 text-center'
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
